import gql from "graphql-tag";

import { cornermarkFragment } from "./cornermark";
import { pageInfoFragment } from "./pageInfo";

export const saleFragment = gql`
  fragment SaleFragment on Sale {
    id
    name
    type
    startDate
    endDate
    value
    saleId
    onlyBuyOne
    rangeType
    showSuperPrice
    showOriginalPrice
    superName
    superPriceText
    superPriceImage
    superPriceImageMb
    prescriptionRequired
    languages {
      id
      language
      languageCode
    }
  }
`;

export const saleListFragment = gql`
  ${saleFragment}
  fragment SaleListFragment on Sale {
    ...SaleFragment
    products {
      totalCount
    }
    variants {
      totalCount
    }
    saleProducts {
      totalCount
    }
    categories(first: 100) {
      edges {
        node {
          products {
            totalCount
          }
        }
      }
    }
    collections(first: 100) {
      edges {
        node {
          products {
            totalCount
          }
        }
      }
    }
  }
`;

export const saleDetailsFragment = gql`
  ${pageInfoFragment}
  ${saleFragment}
  ${cornermarkFragment}
  fragment SaleDetailsFragment on Sale {
    ...SaleFragment
    variants(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          variantId
          name
          sku
          skuName
          isPublished
          product {
            id
            name
          }
          thumbnail {
            url
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    customVariants {
      id
      sortOrder
      usageLimit
      used
      discountPrice
      variant {
        id
        variantId
        name
        sku
        skuName
        isPublished
        product {
          id
          name
        }
        thumbnail {
          url
        }
      }
    }
    saleVariants(
      after: $after
      before: $before
      first: $first
      last: $last
      sortBy: { field: SORT_ORDER, direction: ASC }
      showDefault: true
    ) {
      edges {
        node {
          id
          sortOrder
          usageLimit
          used
          discountPrice
          variant {
            id
            variantId
            name
            sku
            skuName
            isPublished
            product {
              id
              name
            }
            thumbnail {
              url
            }
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    products(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          name
          isPublished
          productType {
            id
            name
          }
          thumbnail {
            url
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    customProducts {
      id
      sortOrder
      usageLimit
      used
      discountPrice
      product {
        id
        name
        isPublished
        productType {
          id
          name
        }
        thumbnail {
          url
        }
      }
    }
    saleProducts(
      after: $after
      before: $before
      first: $first
      last: $last
      sortBy: { field: SORT_ORDER, direction: ASC }
      showDefault: true
    ) {
      edges {
        node {
          id
          sortOrder
          usageLimit
          used
          discountPrice
          product {
            id
            name
            isPublished
            productType {
              id
              name
            }
            thumbnail {
              url
            }
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    categories(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    collections(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    cornerMark {
      ...CornermarkFragment
    }
    excludeVariants(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          variantId
          name
          sku
          skuName
          isPublished
          thumbnail {
            url
          }
          product {
            id
            name
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    excludeProducts(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          name
          productType {
            id
            name
          }
          isPublished
          thumbnail {
            url
          }
        }
      }
      totalCount

      pageInfo {
        ...PageInfoFragment
      }
    }
    excludeCollections(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
    excludeCategories(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const customDetailsFragment = gql`
  ${saleFragment}
  fragment CustomDetailsFragment on Sale {
    ...SaleFragment
    customVariants {
      id
      sortOrder
      usageLimit
      used
      discountPrice
      variant {
        id
        name
        sku
        skuName
        isPublished
        product {
          id
          name
        }
        thumbnail {
          url
        }
      }
    }
    customProducts {
      id
      sortOrder
      usageLimit
      used
      discountPrice
      product {
        id
        name
        isPublished
        productType {
          id
          name
        }
        thumbnail {
          url
        }
      }
    }
  }
`;

export const voucherFragment = gql`
  fragment VoucherFragment on Voucher {
    id
    code
    name
    startDate
    endDate
    usageLimit
    displayOrder
    discountValueType
    discountValue
    applyShopAll
    includeDiscountProduct
    includeComboProduct
    countries {
      code
      country
      regions {
        name
        code
      }
    }
    languages {
      id
      language
      languageCode
    }
    minSpent {
      currency
      amount
    }
    minCheckoutItemsQuantity
  }
`;

export const voucherDetailsFragment = gql`
  ${pageInfoFragment}
  ${voucherFragment}
  fragment VoucherDetailsFragment on Voucher {
    ...VoucherFragment
    isAff
    type
    code
    name
    usageLimit
    used
    applyNumberPerCustomer
    applySpecificCustomer
    applyShopAll
    limitTime
    isRepeatable
    dicountMoney {
      amount
      currency
    }
    minLocalSpent {
      amount
      currency
    }
    displayOrder
    descriptionText
    descriptionSubtext
    descriptionThirdtext
    includeDiscountProduct
    includeComboProduct
    languages {
      id
      language
      languageCode
    }
    variants(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          variantId
          name
          sku
          skuName
          isPublished
          thumbnail {
            url
          }
          product {
            id
            name
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    products(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          name
          productType {
            id
            name
          }
          isPublished
          thumbnail {
            url
          }
        }
      }
      totalCount

      pageInfo {
        ...PageInfoFragment
      }
    }
    collections(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
    categories(after: $after, before: $before, first: $first, last: $last) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
    excludeVariants(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          variantId
          name
          sku
          skuName
          isPublished
          thumbnail {
            url
          }
          product {
            id
            name
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }
    excludeProducts(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          name
          productType {
            id
            name
          }
          isPublished
          thumbnail {
            url
          }
        }
      }
      totalCount

      pageInfo {
        ...PageInfoFragment
      }
    }
    excludeCollections(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
    excludeCategories(
      after: $after
      before: $before
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          name
          products {
            totalCount
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
